import { focusEventNames, ruleSchema } from '@tunasong/schemas'
import type { Rule } from '@tunasong/schemas'

type ProfileConfig = Record<string, unknown> | undefined

/** The single Inbox rule this slice composes (Joni's spec §Scope). */
export const INBOX_CONVERT_RULE_ID = 'inbox-convert-song'
export const CONVERT_ENTITY_TO_SONG_COMMAND_ID = 'convert-entity-to-song'

/**
 * Split stored entries into the rules this client understands and the ones it
 * does not.
 *
 * Parsing the array as a whole (`automationConfigSchema.safeParse`) fails
 * entirely on a single bad entry, which read back as "no rules" and let the
 * writers below persist that emptiness — one unrecognised rule would wipe all
 * of them. Entry-by-entry keeps a bad rule's blast radius to itself, and
 * `unreadable` is carried through writes so a rule written by a newer client is
 * never destroyed by an older one.
 */
const partitionRules = (config: ProfileConfig): { rules: Rule[]; unreadable: unknown[] } => {
  const stored = (config as { rules?: unknown } | undefined)?.rules
  const rules: Rule[] = []
  const unreadable: unknown[] = []

  for (const entry of Array.isArray(stored) ? stored : []) {
    const parsed = ruleSchema.safeParse(entry)
    if (parsed.success) {
      rules.push(parsed.data)
      continue
    }
    unreadable.push(entry)
  }

  return { rules, unreadable }
}

export const readRules = (config: ProfileConfig): Rule[] => partitionRules(config).rules

const isFocusEvent = (event: string): boolean => (focusEventNames as readonly string[]).includes(event)

/** A focus rule watches the entity itself entering/leaving focus (`entered`/`left`). */
export const isFocusRule = (rule: Rule): boolean => isFocusEvent(rule.event)

/** An Inbox rule watches lifecycle events on things *inside* a host (descendants scope). */
export const isInboxRule = (rule: Rule): boolean => rule.scope === 'descendants' && !isFocusEvent(rule.event)

/** The pre-composed "tag a note `song` → convert to Song" rule, disabled for review (spec step 3). */
export const starterInboxRule = (tag = 'song'): Rule => ({
  id: INBOX_CONVERT_RULE_ID,
  event: 'tagged',
  scope: 'descendants',
  enabled: false,
  conditions: [
    { kind: 'type', entityType: 'note' },
    { kind: 'tag', tag },
  ],
  actions: [{ commandId: CONVERT_ENTITY_TO_SONG_COMMAND_ID, args: {} }],
})

const writeRules = (config: ProfileConfig, rules: Rule[]): Record<string, unknown> => ({
  ...config,
  rules: [...rules, ...partitionRules(config).unreadable],
})

export const upsertRule = (config: ProfileConfig, rule: Rule): Record<string, unknown> => {
  const rules = readRules(config)
  const next = rules.some(existing => existing.id === rule.id)
    ? rules.map(existing => (existing.id === rule.id ? rule : existing))
    : [...rules, rule]
  return writeRules(config, next)
}

export const patchRule = (config: ProfileConfig, ruleId: string, patch: Partial<Rule>): Record<string, unknown> =>
  writeRules(
    config,
    readRules(config).map(rule => (rule.id === ruleId ? { ...rule, ...patch } : rule))
  )

export const removeRule = (config: ProfileConfig, ruleId: string): Record<string, unknown> =>
  writeRules(
    config,
    readRules(config).filter(rule => rule.id !== ruleId)
  )

export const setRuleEnabled = (config: ProfileConfig, ruleId: string, enabled: boolean): Record<string, unknown> =>
  patchRule(config, ruleId, { enabled })

/** Rewrite only the `tag` condition's value (the one editable field in the slice). */
export const setRuleTag = (config: ProfileConfig, ruleId: string, tag: string): Record<string, unknown> =>
  writeRules(
    config,
    readRules(config).map(rule =>
      rule.id === ruleId
        ? { ...rule, conditions: rule.conditions.map(condition => (condition.kind === 'tag' ? { ...condition, tag } : condition)) }
        : rule
    )
  )

/** Clear the loop-guard pause and turn the rule back on (spec §3 "Turn back on"). */
export const resumeRule = (config: ProfileConfig, ruleId: string): Record<string, unknown> =>
  patchRule(config, ruleId, { enabled: true, pausedReason: undefined })
